/* ============================================================
   BloomUp cart drawer enhancements
   - instant quantity stepper (batched change.js)
   - size swap on cart lines
   - variant picker for recommended products
   - country picker (no page reload)
   - order summary bottom sheet (shipping + extra discount)
   - scroll-lock watchdog (prevents frozen page after close)

   CHECKOUT BUTTON: owned entirely by assets/bloomup-paybutton.js.
   Nothing here shows or hides the GoKwik / native buttons — two owners made
   them flicker against each other.

   ORDER SUMMARY: all figures come from data attributes rendered by Liquid in
   snippets/item-cart.liquid, so they follow the active market currency and
   the discounts Shopify has actually applied. The applied discount code is
   named on the Extra Discount line; the theme's own green discount pill in
   the footer is hidden, since it duplicated that in a cramped space.

   Shipping is shown from the store's own rule (flat rate below the
   threshold, free at or above) for India only; other markets still say
   "Calculated at checkout", because that rule is an INR rate.

   NOTE ON PRICES: money values may contain markup (e.g. <span class=money>)
   that currency apps rely on, so they are injected as HTML, never escaped.
   Text from products is always escaped.
   ============================================================ */
(function () {
  "use strict";

  var REFS = ["cart-items", "shipping-bar", "cart-bottom"];
  var pending = {};
  var qtyTimer = null;
  var inFlight = false;
  var shipCache = {};

  /* ---------------- helpers ---------------- */
  function drawer() { return document.querySelector("#CartDrawer"); }
  function root() { return document.querySelector("#CartDrawer [data-bu-cart-root]"); }
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  function setLoading(on) {
    var comp = document.querySelector("#CartDrawer cart-items-component");
    if (comp) comp.classList.toggle("cart-items-disabled", !!on);
  }
  function intAttr(el, name) {
    var v = parseInt(el.getAttribute(name), 10);
    return isNaN(v) ? null : v;
  }
  function cartData() {
    var r = root();
    if (!r) return null;
    return {
      mrp: r.getAttribute("data-bu-mrp-money") || "",
      disc: r.getAttribute("data-bu-disc-money") || "",
      discRaw: intAttr(r, "data-bu-disc-raw") || 0,
      extra: r.getAttribute("data-bu-extra-money") || "",
      extraRaw: intAttr(r, "data-bu-extra-raw") || 0,
      extraTitle: r.getAttribute("data-bu-extra-title") || "",
      ship: r.getAttribute("data-bu-ship-money") || "",
      shipRaw: intAttr(r, "data-bu-ship-raw"),
      grand: r.getAttribute("data-bu-grand-money") || "",
      total: r.getAttribute("data-bu-total-money") || "",
      totalRaw: intAttr(r, "data-bu-total-raw") || 0,
      country: r.getAttribute("data-bu-country") || "",
      countryName: r.getAttribute("data-bu-country-name") || "",
      currency: r.getAttribute("data-bu-currency") || "INR"
    };
  }
  /* the store's shipping rule is an INR rate — only trust it for India */
  function knowsShipping(data) {
    return data && data.country === "IN" && data.shipRaw !== null;
  }
  function fmtAmount(amountStr, currency) {
    var n = parseFloat(amountStr);
    if (isNaN(n)) return null;
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency: currency, minimumFractionDigits: 2 }).format(n);
    } catch (e) {
      return currency + " " + n.toFixed(2);
    }
  }
  function productPath(href) {
    if (!href) return "";
    var m = String(href).split("?")[0].match(/\/products\/[^/]+/);
    return m ? m[0] : "";
  }
  function pruneReco(scope) {
    var block = scope.querySelector(".bu-reco-wrap");
    if (!block) return;
    var inCart = {};
    scope.querySelectorAll(".hdt-mini-cart__item .hdt-mini-cart__title[href]").forEach(function (a) {
      var p = productPath(a.getAttribute("href"));
      if (p) inCart[p] = true;
    });
    block.querySelectorAll(".bu-reco-card").forEach(function (card) {
      var link = card.querySelector("a[href]");
      var p = productPath(link ? link.getAttribute("href") : "");
      if (p && inCart[p]) card.remove();
    });
    if (!block.querySelector(".bu-reco-card")) block.remove();
  }

  /* ---------------- partial re-render ---------------- */
  function partialRerender(done) {
    var host = document.querySelector("hdt-cart-drawer");
    var sid = host ? host.getAttribute("section-id") : null;
    if (!sid) { window.location.reload(); return; }
    fetch("/?sections=" + encodeURIComponent(sid), { headers: { "Accept": "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        var html = j && j[sid];
        if (!html) { window.location.reload(); return; }
        var doc = new DOMParser().parseFromString(html, "text/html");
        var d = drawer();
        var scroller = document.querySelector("#CartDrawer [data-hdt-scroll-me]");
        var scrollTop = scroller ? scroller.scrollTop : 0;

        var keepReco = null;
        var liveReco = d ? d.querySelector(".bu-reco-wrap") : null;
        if (liveReco) {
          var liveSeed = liveReco.getAttribute("data-bu-reco-seed");
          var nextSlot = doc.querySelector(".bu-reco-auto[data-bu-reco-seed]");
          var nextSeed = nextSlot ? nextSlot.getAttribute("data-bu-reco-seed") : null;
          var seedChanged = liveSeed && nextSeed && liveSeed !== nextSeed;
          if (!seedChanged) {
            keepReco = liveReco;
            var rs = liveReco.querySelector(".bu-reco-scroller");
            if (rs) keepReco.__buScrollLeft = rs.scrollLeft;
          }
        }

        var replaced = false;
        REFS.forEach(function (name) {
          var fresh = doc.querySelector('[ref="' + name + '"]');
          var cur = document.querySelector('#CartDrawer [ref="' + name + '"]');
          if (fresh && cur) { cur.innerHTML = fresh.innerHTML; replaced = true; }
        });
        if (!replaced) {
          var freshD = doc.querySelector("#CartDrawer");
          if (freshD && d) { d.innerHTML = freshD.innerHTML; } else { window.location.reload(); return; }
        }

        var d2 = drawer();
        if (keepReco && d2) {
          var target =
            d2.querySelector(".bu-reco-auto[data-bu-reco-seed]") ||
            d2.querySelector(".bu-reco-wrap");
          if (target && target !== keepReco) {
            target.replaceWith(keepReco);
            var back = keepReco.querySelector(".bu-reco-scroller");
            if (back && keepReco.__buScrollLeft) back.scrollLeft = keepReco.__buScrollLeft;
          }
          pruneReco(d2);
        }

        var freshHost = doc.querySelector("hdt-cart-drawer");
        if (freshHost && host) host.setAttribute("data-count", freshHost.getAttribute("data-count") || "");
        var newScroller = document.querySelector("#CartDrawer [data-hdt-scroll-me]");
        if (newScroller) newScroller.scrollTop = scrollTop;
        buildSummary();
        if (done) done();
      })
      .catch(function () { window.location.reload(); });
  }

  /* ---------------- quantity stepper ---------------- */
  function commitQty(input, val) {
    var min = parseInt(input.getAttribute("min"), 10) || 1;
    var max = parseInt(input.getAttribute("data-max"), 10) || 9999;
    if (isNaN(val)) val = min;
    if (val > max) val = max;
    if (val < min) val = min;
    input.value = val;
    var row = input.closest(".hdt-mini-cart__item");
    var badge = row ? row.querySelector(".bu-qty-badge") : null;
    if (badge) badge.textContent = val;
    var line = input.getAttribute("data-index");
    if (!line) return;
    pending[line] = val;
    scheduleQty(320);
  }
  function flushQty() {
    qtyTimer = null;
    if (inFlight) { scheduleQty(120); return; }
    var lines = Object.keys(pending);
    if (!lines.length) return;
    var batch = {};
    lines.forEach(function (ln) { batch[ln] = pending[ln]; });
    pending = {};
    inFlight = true;
    setLoading(true);
    var chain = Promise.resolve();
    lines.forEach(function (ln) {
      chain = chain.then(function () {
        return fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ line: parseInt(ln, 10), quantity: batch[ln] })
        });
      });
    });
    chain.then(function () {
      inFlight = false;
      partialRerender(function () { setLoading(false); });
    }).catch(function () {
      inFlight = false;
      partialRerender(function () { setLoading(false); });
    });
  }
  function scheduleQty(ms) {
    if (qtyTimer) clearTimeout(qtyTimer);
    qtyTimer = setTimeout(flushQty, ms == null ? 320 : ms);
  }
  document.addEventListener("click", function (e) {
    var btn = e.target && e.target.closest ? e.target.closest("#CartDrawer .hdt-quantity-cart-item button") : null;
    if (!btn) return;
    var wrap = btn.closest(".hdt-quantity-cart-item");
    var input = wrap ? wrap.querySelector(".hdt-quantity-input") : null;
    if (!input) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    var isPlus = btn.classList.contains("is--plus") || btn.name === "plus";
    var cur = parseInt(input.value, 10) || 1;
    var next = isPlus ? cur + 1 : cur - 1;
    if (next === cur) return;
    commitQty(input, next);
  }, true);

  document.addEventListener("change", function (e) {
    var input = e.target && e.target.closest ? e.target.closest("#CartDrawer .hdt-quantity-input") : null;
    if (!input) return;
    e.stopPropagation();
    commitQty(input, parseInt(input.value, 10));
  }, true);

  document.addEventListener("keydown", function (e) {
    if (e.key !== "Enter") return;
    var input = e.target && e.target.closest ? e.target.closest("#CartDrawer .hdt-quantity-input") : null;
    if (!input) return;
    e.preventDefault();
    e.stopPropagation();
    commitQty(input, parseInt(input.value, 10));
    input.blur();
  }, true);

  /* ---------------- size swap on cart lines ---------------- */
  document.addEventListener("change", function (e) {
    var sel = e.target && e.target.closest ? e.target.closest(".bu-size-select") : null;
    if (!sel) return;
    var oldId = sel.getAttribute("data-current");
    var newId = sel.value;
    var qty = parseInt(sel.getAttribute("data-qty") || "1", 10) || 1;
    if (!newId || newId === oldId) return;
    sel.disabled = true;
    setLoading(true);
    fetch("/cart/change.js", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: oldId, quantity: 0 }) })
      .then(function (r) {
        if (!r.ok) throw new Error("remove failed");
        return fetch("/cart/add.js", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: parseInt(newId, 10), quantity: qty }) });
      })
      .then(function (r) {
        if (!r.ok) {
          return fetch("/cart/add.js", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: parseInt(oldId, 10), quantity: qty }) });
        }
      })
      .then(function () { partialRerender(function () { setLoading(false); }); })
      .catch(function () { partialRerender(function () { setLoading(false); }); });
  });

  /* ---------------- variant picker for recommended products ---------------- */
  function variantSheetEl() {
    var d = drawer();
    if (!d) return null;
    var el = d.querySelector(".bu-variant-sheet");
    if (el) return el;
    el = document.createElement("div");
    el.className = "bu-variant-sheet";
    el.setAttribute("role", "dialog");
    el.setAttribute("aria-label", "Select variant");
    el.innerHTML =
      '<div class="bu-vs-panel">' +
      '<div class="bu-vs-head"><span class="bu-vs-title">Select Variant</span>' +
      '<button type="button" class="bu-vs-close" aria-label="Close">&times;</button></div>' +
      '<div class="bu-vs-body"></div>' +
      "</div>";
    d.appendChild(el);
    return el;
  }
  function openVariantSheet(card) {
    var dataEl = card.querySelector(".bu-variant-json");
    if (!dataEl) return;
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (err) { return; }
    if (!data || !data.variants || !data.variants.length) return;
    var sheet = variantSheetEl();
    if (!sheet) return;
    var body = sheet.querySelector(".bu-vs-body");
    var img = data.image ? '<img src="' + esc(data.image) + '" alt="" loading="lazy">' : "";
    var rows = data.variants.map(function (v) {
      var price =
        (v.has_compare ? '<s class="bu-vs-mrp">' + v.compare + "</s>" : "") +
        '<span class="bu-vs-price">' + v.price + "</span>" +
        (v.has_compare ? '<span class="bu-vs-pct">(' + v.pct + "% OFF)</span>" : "");
      var action = v.available
        ? '<div class="bu-vs-qty"><button type="button" class="bu-vs-minus" aria-label="Decrease">\u2212</button>' +
          '<input class="bu-vs-input" type="number" value="1" min="1" inputmode="numeric" aria-label="Quantity">' +
          '<button type="button" class="bu-vs-plus" aria-label="Increase">+</button></div>' +
          '<button type="button" class="bu-vs-add" data-id="' + v.id + '">Add to cart</button>'
        : '<span class="bu-vs-sold">Sold out</span>';
      return (
        '<div class="bu-vs-row' + (v.available ? "" : " is-sold") + '">' +
        '<div class="bu-vs-media">' + img + "</div>" +
        '<div class="bu-vs-info"><p class="bu-vs-name">' + esc(data.title) + "</p>" +
        '<span class="bu-vs-chip">' + esc(v.title) + "</span></div>" +
        '<div class="bu-vs-actions"><div class="bu-vs-prices">' + price + "</div>" + action + "</div>" +
        "</div>"
      );
    }).join("");
    body.innerHTML = rows;
    body.scrollTop = 0;
    requestAnimationFrame(function () { sheet.classList.add("bu-open"); });
  }
  function closeVariantSheet() {
    var d = drawer();
    var sheet = d ? d.querySelector(".bu-variant-sheet") : null;
    if (sheet) sheet.classList.remove("bu-open");
  }
  document.addEventListener("click", function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var opener = t.closest("#CartDrawer .bu-open-variants");
    if (opener) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      var card = opener.closest(".bu-reco-card");
      if (card) openVariantSheet(card);
      return;
    }
    if (t.closest(".bu-vs-close")) { closeVariantSheet(); return; }
    var sheetEl = t.closest(".bu-variant-sheet");
    if (sheetEl && !t.closest(".bu-vs-panel")) { closeVariantSheet(); return; }

    var step = t.closest(".bu-vs-minus, .bu-vs-plus");
    if (step) {
      e.preventDefault();
      var input = step.parentNode.querySelector(".bu-vs-input");
      if (input) {
        var val = parseInt(input.value, 10) || 1;
        val = step.classList.contains("bu-vs-plus") ? val + 1 : val - 1;
        if (val < 1) val = 1;
        if (val > 99) val = 99;
        input.value = val;
      }
      return;
    }
    var add = t.closest(".bu-vs-add");
    if (add) {
      e.preventDefault();
      e.stopPropagation();
      var id = parseInt(add.getAttribute("data-id"), 10);
      if (!id) return;
      var row = add.closest(".bu-vs-row");
      var qtyInput = row ? row.querySelector(".bu-vs-input") : null;
      var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      add.disabled = true;
      add.textContent = "Adding\u2026";
      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: id, quantity: qty })
      })
        .then(function (r) {
          if (!r.ok) throw new Error("add failed");
          closeVariantSheet();
          partialRerender();
        })
        .catch(function () {
          add.disabled = false;
          add.textContent = "Try again";
        });
      return;
    }
  }, true);

  /* ---------------- country picker (no reload) ---------------- */
  function mountCountry() {
    var tpl = document.getElementById("bu-country-template");
    var d = drawer();
    if (!tpl || !d) return;
    var title = d.querySelector(".hdt-mini-cart__header-title");
    if (!title || !title.parentNode) return;
    if (d.querySelector(".bu-country-form")) return;
    var node = tpl.content ? tpl.content.cloneNode(true) : null;
    if (!node) return;
    var holder = document.createElement("div");
    holder.className = "bu-country-holder";
    holder.appendChild(node);
    title.parentNode.insertBefore(holder, title.nextSibling);
  }
  document.addEventListener("change", function (e) {
    var sel = e.target && e.target.closest ? e.target.closest(".bu-country-select") : null;
    if (!sel) return;
    e.preventDefault();
    e.stopPropagation();
    var form = sel.closest("form");
    if (!form) return;
    var code = sel.value;
    sel.disabled = true;
    setLoading(true);
    var fd = new FormData();
    fd.append("form_type", "localization");
    fd.append("utf8", "\u2713");
    fd.append("_method", "put");
    fd.append("country_code", code);
    fd.append("return_to", window.location.pathname + window.location.search);
    fetch(form.getAttribute("action") || "/localization", {
      method: "POST",
      body: fd,
      headers: { "Accept": "text/html" },
      redirect: "follow",
      credentials: "same-origin"
    })
      .then(function () {
        shipCache = {};
        partialRerender(function () {
          sel.disabled = false;
          setLoading(false);
          var fresh = document.querySelector(".bu-country-select");
          if (fresh) fresh.value = code;
          var sheet = document.querySelector("#CartDrawer .bu-summary-sheet");
          if (sheet && sheet.classList.contains("bu-open")) refreshShipping();
        });
      })
      .catch(function () {
        sel.disabled = false;
        setLoading(false);
      });
  }, true);

  /* ---------------- GoKwik arming ----------------
     Only arms the button. Showing/hiding is bloomup-paybutton.js's job. */
  window.__buGokwikReady = window.__buGokwikReady || false;
  window.addEventListener("gokwikLoaded", function () {
    window.__buGokwikReady = true;
    armCheckout(false);
  });
  function armCheckout(force) {
    var btns = document.querySelectorAll("#CartDrawer .gokwik-checkout button");
    if (!btns.length) return;
    var agree = document.querySelector("#CartDrawer-Form_agree");
    var agreed = !agree || agree.checked;
    for (var i = 0; i < btns.length; i++) {
      var btn = btns[i];
      if (agreed && !btn.classList.contains("gkactive2")) btn.classList.add("gkactive2");
      if (window.__buGokwikReady) {
        if (btn.disabled) btn.disabled = false;
        if (btn.classList.contains("disabled")) btn.classList.remove("disabled");
        if (btn.hasAttribute("data-bu-fallback")) btn.removeAttribute("data-bu-fallback");
      } else if (force) {
        if (btn.disabled) btn.disabled = false;
        if (btn.classList.contains("disabled")) btn.classList.remove("disabled");
        if (!btn.hasAttribute("data-bu-fallback")) btn.setAttribute("data-bu-fallback", "1");
      }
    }
  }

  /* ---------------- shipping estimate ----------------
     Only used for markets where we do not know the rule. India is served
     straight from the Liquid values, with no network call. */
  function getShipping(cb) {
    var d = cartData();
    if (!d) { cb(null); return; }
    var key = d.country + ":" + d.totalRaw;
    if (shipCache[key] !== undefined) { cb(shipCache[key]); return; }
    if (!d.countryName) { cb(null); return; }
    fetch("/cart/shipping_rates.json?shipping_address%5Bcountry%5D=" + encodeURIComponent(d.countryName))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var rates = j && j.shipping_rates ? j.shipping_rates : null;
        var out = null;
        if (rates && rates.length) {
          var best = rates[0];
          for (var i = 1; i < rates.length; i++) {
            if (parseFloat(rates[i].price) < parseFloat(best.price)) best = rates[i];
          }
          out = { price: parseFloat(best.price), label: fmtAmount(best.price, d.currency), name: best.name };
          if (out.price === 0) out.label = "FREE";
        }
        shipCache[key] = out;
        cb(out);
      })
      .catch(function () { shipCache[key] = null; cb(null); });
  }
  function refreshShipping() {
    var data = cartData();
    if (knowsShipping(data)) return;   /* already rendered from Liquid */
    getShipping(function (rate) {
      var label = rate ? (rate.label || "FREE") : "Calculated at checkout";
      var cell = document.querySelector("#CartDrawer .bu-ship-row span:last-child");
      if (cell) {
        cell.textContent = label;
        if (rate && rate.price === 0) { cell.style.color = "#12a150"; cell.style.fontStyle = "normal"; cell.style.fontWeight = "700"; }
      }
    });
  }

  /* ---------------- order summary sheet ---------------- */
  function row(label, value, opts) {
    opts = opts || {};
    var vStyle = "font-weight:600;color:#111827;";
    if (opts.green) vStyle = "font-weight:700;color:#12a150;";
    if (opts.mutedItalic) vStyle = "font-style:italic;color:#7a8090;font-weight:500;";
    var size = opts.big ? "1.6rem" : "1.35rem";
    var lStyle = opts.big ? "font-weight:700;color:#111827;" : "color:#3a4150;";
    var attr = opts.cls ? ' class="' + opts.cls + '"' : "";
    return '<div' + attr + ' style="display:flex;align-items:center;justify-content:space-between;gap:1rem;font-size:' + size + ';margin:0.5rem 0;"><span style="' + lStyle + '">' + label + '</span><span style="' + vStyle + '">' + value + "</span></div>";
  }
  /* "Extra Discount  BLOOMUP10" — names the code Shopify actually applied */
  function extraLabel(data) {
    var label = "Extra Discount";
    var title = (data.extraTitle || "").trim();
    if (!title) return label;
    if (title.length > 16) title = title.slice(0, 16) + "\u2026";
    return (
      label +
      ' <span class="bu-code-chip">' + esc(title.toUpperCase()) + "</span>"
    );
  }
  function ensureStyle() {
    if (document.getElementById("bu-summary-style")) return;
    var st = document.createElement("style");
    st.id = "bu-summary-style";
    st.textContent =
      ".bu-summary-sheet{position:absolute;left:0;right:0;bottom:0;background:#fff;border-radius:2rem 2rem 0 0;box-shadow:0 -0.8rem 2.4rem rgba(16,24,40,.18);padding:0.6rem 1.8rem 1.6rem;transform:translateY(110%);opacity:0;pointer-events:none;transition:transform .28s ease,opacity .2s ease;z-index:6;will-change:transform;}" +
      ".bu-summary-sheet.bu-open{transform:translateY(0);opacity:1;pointer-events:auto;}" +
      ".bu-sheet-handle{width:5.2rem;height:0.5rem;border-radius:999px;background:#d9dce3;margin:0.6rem auto 1.2rem;cursor:pointer;}" +
      ".bu-sheet-checkout{margin-top:1.2rem;}" +
      ".bu-sheet-checkout .hdt-mini-cart__view-checkout{width:100%;margin:0;}" +
      ".bu-sum-toggle{background:none;border:0;padding:0;font-size:1.2rem;font-weight:600;color:#111827;text-decoration:underline;text-underline-offset:0.3rem;cursor:pointer;text-align:left;display:inline-block;margin-top:0.2rem;}" +
      ".bu-code-chip{display:inline-block;margin-inline-start:0.6rem;padding:0.1rem 0.6rem;border:0.1rem dashed #12a150;border-radius:0.5rem;font-size:1.05rem;font-weight:700;letter-spacing:0.02em;color:#0f7a3d;background:#f2fbf5;vertical-align:middle;}" +
      "#CartDrawer cart-items-component.cart-items-disabled{opacity:1!important;}" +
      "#CartDrawer .cart-items-disabled .hdt-mini-cart__items{opacity:.5;transition:opacity .18s ease;}" +
      /* the theme's green discount pill in the footer duplicates the Extra
         Discount line and crowds the Pay Now button — hide it there only */
      "#CartDrawer .hdt-cart-totals-discounts .hdt-discounts," +
      "#CartDrawer .hdt-cart-totals-discounts .hdt-cart_discounts," +
      "#CartDrawer .hdt-cart-totals-discounts > ul{display:none!important;}" +
      "@media (max-width:520px){.bu-country-select{max-width:10.5rem;font-size:1.1rem;padding:0.4rem 2.2rem 0.4rem 0.8rem;}.bu-country-holder{margin-inline-end:0.8rem;}}";
    document.head.appendChild(st);
  }
  function parts() {
    var d = drawer();
    if (!d) return {};
    return {
      sheet: d.querySelector(".bu-summary-sheet"),
      toggle: d.querySelector(".bu-sum-toggle"),
      checkout: d.querySelector(".hdt-mini-cart__view-checkout"),
      anchor: d.querySelector(".bu-co-anchor")
    };
  }
  function openSheet() {
    try {
      var p = parts();
      if (!p.sheet) return;
      var slot = p.sheet.querySelector(".bu-sheet-checkout");
      if (slot && p.checkout) slot.appendChild(p.checkout);
      p.sheet.classList.add("bu-open");
      if (p.toggle) { p.toggle.setAttribute("aria-expanded", "true"); p.toggle.innerHTML = "Estimated Total \u25B4"; }
      refreshShipping();
    } catch (err) {}
  }
  function closeSheet() {
    try {
      var p = parts();
      if (!p.sheet) return;
      p.sheet.classList.remove("bu-open");
      if (p.anchor && p.checkout && p.anchor.parentNode && p.checkout.parentNode !== p.anchor.parentNode) {
        p.anchor.parentNode.insertBefore(p.checkout, p.anchor.nextSibling);
      }
      if (p.toggle) { p.toggle.setAttribute("aria-expanded", "false"); p.toggle.innerHTML = "Estimated Total \u25BE"; }
    } catch (err) {}
  }
  function releaseLock() {
    try {
      var d = drawer();
      if (d && (d.hasAttribute("open") || d.open)) return;
      if (document.querySelector("dialog[open][scroll-lock], dialog.is--opened[scroll-lock]")) return;
      var b = document.body, h = document.documentElement;
      [b, h].forEach(function (el) {
        if (!el) return;
        if (el.style.overflow === "hidden") el.style.overflow = "";
        if (el.style.position === "fixed") { el.style.position = ""; el.style.top = ""; el.style.width = ""; }
        if (el.style.paddingRight) el.style.paddingRight = "";
        el.classList.remove("hdt-scroll-lock", "scroll-lock", "is--scroll-lock", "overflow-hidden", "no-scroll");
      });
      if (b) b.removeAttribute("data-scroll-lock");
    } catch (err) {}
  }
  function onDrawerClose() {
    closeSheet();
    closeVariantSheet();
    setTimeout(releaseLock, 60);
    setTimeout(releaseLock, 400);
  }
  function buildSummary() {
    var d = drawer();
    if (!d) return;
    mountCountry();
    armCheckout(window.__buForceArm === true);
    var totalsWrap = d.querySelector(".hdt-cart-totals-discounts");
    if (!totalsWrap) return;
    if (totalsWrap.querySelector(".bu-order-summary")) return;
    var data = cartData();
    if (!data) return;
    var defaultRow = totalsWrap.querySelector(".hdt-mini-cart__totals");
    if (defaultRow) defaultRow.style.display = "none";
    var taxRow = d.querySelector(".hdt-cart-tax");
    if (taxRow) taxRow.style.display = "none";

    /* shipping + grand total: exact for India, estimated elsewhere */
    var known = knowsShipping(data);
    var shipRow;
    if (!known) {
      shipRow = row("Shipping", "Calculated at checkout", { mutedItalic: true, cls: "bu-ship-row" });
    } else if (data.shipRaw === 0) {
      shipRow = row("Shipping", "FREE", { green: true, cls: "bu-ship-row" });
    } else {
      shipRow = row("Shipping", data.ship, { cls: "bu-ship-row" });
    }
    var grandMoney = known && data.grand ? data.grand : data.total;

    var bottom = d.querySelector(".hdt-mini-cart__bottom");
    if (bottom) {
      bottom.style.position = "relative";
      var co = d.querySelector(".hdt-mini-cart__view-checkout");
      if (co && !d.querySelector(".bu-co-anchor")) {
        var anchor = document.createElement("span");
        anchor.className = "bu-co-anchor";
        anchor.hidden = true;
        co.parentNode.insertBefore(anchor, co);
      }
      var oldSheet = bottom.querySelector(".bu-summary-sheet");
      if (oldSheet) oldSheet.remove();
      var sheet = document.createElement("div");
      sheet.className = "bu-summary-sheet";
      sheet.innerHTML =
        '<div class="bu-sheet-handle" role="button" aria-label="Close order summary"></div>' +
        '<p style="font-size:1.7rem;font-weight:700;color:#111827;margin:0 0 0.8rem;">Order Summary</p>' +
        row("Subtotal", data.mrp) +
        (data.discRaw > 0 ? row("Discount on MRP", "- " + data.disc, { green: true }) : "") +
        (data.extraRaw > 0 ? row(extraLabel(data), "- " + data.extra, { green: true }) : "") +
        shipRow +
        '<div style="border-top:0.1rem solid #eef0f4;margin:0.9rem 0;"></div>' +
        row("Grand Total", grandMoney, { big: true }) +
        '<div class="bu-sheet-checkout"></div>';
      bottom.appendChild(sheet);
    }
    var el = document.createElement("div");
    el.className = "bu-order-summary";
    el.innerHTML =
      '<span style="display:block;font-size:2rem;font-weight:800;color:#111827;line-height:1.2;">' + grandMoney + "</span>" +
      '<button type="button" class="bu-sum-toggle" aria-expanded="false">Estimated Total \u25BE</button>';
    totalsWrap.appendChild(el);
  }

  /* ---------------- global click handling ---------------- */
  document.addEventListener("click", function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var gk = t.closest("#CartDrawer .gokwik-checkout button[data-bu-fallback]");
    if (gk && !window.__buGokwikReady) {
      e.preventDefault();
      e.stopPropagation();
      window.location.href = "/checkout";
      return;
    }
    var toggle = t.closest(".bu-sum-toggle");
    if (toggle) {
      var sheet = document.querySelector("#CartDrawer .bu-summary-sheet");
      if (sheet) { sheet.classList.contains("bu-open") ? closeSheet() : openSheet(); }
      return;
    }
    if (t.closest(".bu-sheet-handle")) { closeSheet(); return; }
    if (t.closest('#CartDrawer [close-dialog], #CartDrawer .hdt-mini-cart__header-close, #CartDrawer [aria-label="Close"]')) {
      closeSheet();
      closeVariantSheet();
      setTimeout(releaseLock, 120);
      setTimeout(releaseLock, 500);
      return;
    }
    if (t.closest("#CartDrawer [data-hdt-scroll-me]") && !t.closest(".bu-variant-sheet") && !t.closest(".bu-gift-sheet")) { closeSheet(); }
  }, true);
  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" || e.key === "Esc") {
      var vs = document.querySelector("#CartDrawer .bu-variant-sheet.bu-open");
      if (vs) { closeVariantSheet(); return; }
      onDrawerClose();
    }
  });

  /* ---------------- init ---------------- */
  function init() {
    ensureStyle();
    buildSummary();
    setTimeout(function () { armCheckout(false); }, 1200);
    setTimeout(function () { window.__buForceArm = true; armCheckout(true); }, 3500);
    var d = drawer();
    if (d) {
      d.addEventListener("close", onDrawerClose);
      d.addEventListener("cancel", onDrawerClose);
      if (window.MutationObserver) {
        new MutationObserver(function () {
          if (!d.hasAttribute("open") && !d.open) { onDrawerClose(); } else { mountCountry(); }
        }).observe(d, { attributes: true, attributeFilter: ["open", "class"] });
      }
    }
    var host = document.querySelector("hdt-cart-drawer");
    if (host && window.MutationObserver) {
      var scheduled = false;
      new MutationObserver(function () {
        if (scheduled) return;
        scheduled = true;
        setTimeout(function () { scheduled = false; buildSummary(); }, 60);
      }).observe(host, { childList: true, subtree: true });
    }
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  window.BUCart = { rerender: partialRerender, setLoading: setLoading };
})();